import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Icon,
  InputAdornment,
  Switch,
  FormControlLabel,
  Button,
  Alert,
  useTheme,
  alpha,
} from "@mui/material";
import { toast } from "react-toastify";
import { useDebounce } from "../../../../hooks/useDebounce";
import { stockValuationService } from "../../services/stock-valuation.service";
import {
  StockValuationEntry,
  StockValuationFilters,
  StockValuationResponse,
  StockValuationSummary,
} from "../../types/stock-valuation.types";
import { AppTable } from "../../../../components/AppTable";
import { TableColumn, TableToolbarActions } from "../../../../app.interface";

const THEME_COLORS = {
  primary: "#0891B2",
  success: "#059669",
  error: "#DC2626",
  warning: "#F59E0B",
  background: {
    light: "#F8FAFC",
    card: "#FFFFFF",
  },
  border: {
    light: alpha("#0891B2", 0.1),
    main: alpha("#0891B2", 0.2),
  },
  text: {
    primary: "#1E293B",
    secondary: "#64748B",
  },
};

interface StockValuationState {
  valuations: StockValuationEntry[];
  loading: boolean;
  error: string | null;
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  summary: StockValuationSummary | null;
}

const StockValuation: React.FC = () => {
  const theme = useTheme();

  // State management
  const [state, setState] = useState<StockValuationState>({
    valuations: [],
    loading: false,
    error: null,
    total: 0,
    page: 1,
    pageSize: 20,
    totalPages: 0,
    summary: null,
  });

  // Filters state
  const [filters, setFilters] = useState<StockValuationFilters>({});
  const [searchTerm, setSearchTerm] = useState("");
  const [showExpiredOnly, setShowExpiredOnly] = useState(false);
  const [showLowStockOnly, setShowLowStockOnly] = useState(false);
  const [sortField, setSortField] = useState("total_value");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  // Debounced search
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  // Load stock valuation data
  const loadValuations = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const requestFilters: StockValuationFilters = {
        ...filters,
        search: debouncedSearchTerm || undefined,
        only_expired: showExpiredOnly || undefined,
        only_low_stock: showLowStockOnly || undefined,
      };

      const response = await stockValuationService.getStockValuation({
        page: state.page,
        page_size: state.pageSize,
        filters: requestFilters,
        sort_field: sortField,
        sort_order: sortOrder,
      });

      if (response.data.success) {
        const data = response.data.data;
        setState((prev) => ({
          ...prev,
          valuations: data.valuations,
          total: data.total,
          totalPages: data.total_pages,
          summary: data.summary,
          loading: false,
        }));
      } else {
        throw new Error(
          response.data.message || "Failed to load stock valuation"
        );
      }
    } catch (error: any) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: error.message || "Failed to load stock valuation",
      }));
      toast.error("Failed to load stock valuation");
    }
  }, [
    filters,
    debouncedSearchTerm,
    showExpiredOnly,
    showLowStockOnly,
    state.page,
    state.pageSize,
    sortField,
    sortOrder,
  ]);

  // Effects
  useEffect(() => {
    loadValuations();
  }, [loadValuations]);

  // Event handlers
  const handleFilterChange = (
    field: keyof StockValuationFilters,
    value: any
  ) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value || undefined,
    }));
    setState((prev) => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (page: number) => {
    setState((prev) => ({ ...prev, page }));
  };

  const handlePageSizeChange = (pageSize: number) => {
    setState((prev) => ({ ...prev, pageSize, page: 1 }));
  };

  const handleSort = (field: string, direction: "asc" | "desc") => {
    setSortField(field);
    setSortOrder(direction);
    setState((prev) => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    setFilters({});
    setSearchTerm("");
    setShowExpiredOnly(false);
    setShowLowStockOnly(false);
    setState((prev) => ({ ...prev, page: 1 }));
  };

  // Utility function for currency formatting
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  // Column definitions for AppTable
  const getColumns = (): TableColumn[] => {
    return [
      {
        id: "product_id",
        header: "Item ID",
        field: "product_id",
        type: "text",
        width: "w-32",
        sortable: true,
      },
      {
        id: "item_name",
        header: "Item Name",
        field: "item_name",
        type: "text",
        sortable: true,
        width: "min-w-48",
      },
      {
        id: "category",
        header: "Category",
        field: "category",
        type: "text",
        sortable: true,
        accessor: (row) => row.category || "-",
      },
      {
        id: "warehouse_location",
        header: "Warehouse",
        field: "warehouse_location",
        type: "text",
        sortable: true,
        accessor: (row) => row.warehouse_location || "-",
      },
      {
        id: "quantity_available",
        header: "Quantity Available",
        field: "quantity_available",
        type: "text",
        align: "right",
        sortable: true,
        accessor: (row) =>
          `${row.quantity_available?.toLocaleString() || 0} ${
            row.unit_of_measurement
          }`,
      },
      {
        id: "unit_cost",
        header: "Unit Cost (FIFO)",
        field: "unit_cost",
        type: "money",
        align: "right",
        sortable: true,
      },
      {
        id: "total_value",
        header: "Total Value",
        field: "total_value",
        type: "money",
        align: "right",
        sortable: true,
      },
      {
        id: "unit_of_measurement",
        header: "UOM",
        field: "unit_of_measurement",
        type: "text",
        width: "w-20",
      },
      {
        id: "aging_days",
        header: "Aging (Days)",
        field: "aging_days",
        type: "text",
        align: "center",
        sortable: true,
        accessor: (row: Record<string, any>) => `${row.aging_days || 0} days`,
      },
      {
        id: "last_movement_date",
        header: "Last Movement",
        field: "last_movement_date",
        type: "datetime",
        dateFormat: "custom",
        customDateFormat: "MMM d, yyyy",
        sortable: true,
      },
    ];
  };

  // Toolbar actions for AppTable
  const toolbarActions: TableToolbarActions = {
    refresh: {
      label: "Refresh Data",
      is_disabled: state.loading,
      onClick: loadValuations,
    },
    export: {
      label: "Export Data",
      is_disabled: state.loading || state.valuations.length === 0,
      options: {
        formats: ["csv", "excel"],
        filename: "stock-valuation",
      },
      datasource: "pos_stock_valuation",
      filters: [
        ...(filters.category
          ? [
              {
                id: "category",
                title: "Category",
                field: "category",
                operator: "equals",
                value: filters.category,
              },
            ]
          : []),
        ...(filters.warehouse_location
          ? [
              {
                id: "warehouse",
                title: "Warehouse",
                field: "warehouse_location",
                operator: "equals",
                value: filters.warehouse_location,
              },
            ]
          : []),
        ...(debouncedSearchTerm
          ? [
              {
                id: "search",
                title: "Search",
                field: "item_name",
                operator: "contains",
                value: debouncedSearchTerm,
              },
            ]
          : []),
        ...(showExpiredOnly
          ? [
              {
                id: "expired_only",
                title: "Expired Only",
                field: "only_expired",
                operator: "equals",
                value: true,
              },
            ]
          : []),
        ...(showLowStockOnly
          ? [
              {
                id: "low_stock_only",
                title: "Low Stock Only",
                field: "only_low_stock",
                operator: "equals",
                value: true,
              },
            ]
          : []),
      ],
    },
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h4"
          gutterBottom
          sx={{ color: THEME_COLORS.text.primary }}
        >
          Stock Valuation Report
        </Typography>
        <Typography variant="body2" sx={{ color: THEME_COLORS.text.secondary }}>
          Current inventory valuation with aging analysis and stock tracking
        </Typography>
      </Box>

      {/* Summary Cards */}
      {state.summary && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="primary.main">
                  Total Items
                </Typography>
                <Typography variant="h5">
                  {state.summary.total_items.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Inventory Items
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="success.main">
                  Total Quantity
                </Typography>
                <Typography variant="h5">
                  {state.summary.total_quantity.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Units in Stock
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="warning.main">
                  Total Value
                </Typography>
                <Typography variant="h5">
                  {formatCurrency(state.summary.total_value)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Inventory Value
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="info.main">
                  Avg. Aging
                </Typography>
                <Typography variant="h5">
                  {Math.round(state.summary.average_aging_days)} days
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Average Age
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Filters
        </Typography>
        <Grid container spacing={2}>
          {/* Search */}
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Item name, product ID..."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon>search</Icon>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* Category */}
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={filters.category || ""}
                label="Category"
                onChange={(e) => handleFilterChange("category", e.target.value)}
              >
                <MenuItem value="">All Categories</MenuItem>
                <MenuItem value="ingredients">Ingredients</MenuItem>
                <MenuItem value="beverages">Beverages</MenuItem>
                <MenuItem value="packaging">Packaging</MenuItem>
                <MenuItem value="supplies">Supplies</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Warehouse */}
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Warehouse</InputLabel>
              <Select
                value={filters.warehouse_location || ""}
                label="Warehouse"
                onChange={(e) =>
                  handleFilterChange("warehouse_location", e.target.value)
                }
              >
                <MenuItem value="">All Warehouses</MenuItem>
                <MenuItem value="Main Storage">Main Storage</MenuItem>
                <MenuItem value="Secondary Storage">Secondary Storage</MenuItem>
                <MenuItem value="Main Warehouse">Main Warehouse</MenuItem>
                <MenuItem value="Cold Storage">Cold Storage</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Clear Filters Button */}
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              onClick={clearFilters}
              sx={{ height: "56px" }}
            >
              Clear All
            </Button>
          </Grid>
        </Grid>

        {/* Quick Filters */}
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={showExpiredOnly}
                  onChange={(e) => setShowExpiredOnly(e.target.checked)}
                />
              }
              label="Show expired items only"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={showLowStockOnly}
                  onChange={(e) => setShowLowStockOnly(e.target.checked)}
                />
              }
              label="Show low stock items only"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Error Display */}
      {state.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {state.error}
        </Alert>
      )}

      {/* AppTable */}
      <AppTable
        data={state.valuations.map((valuation) => ({
          ...valuation,
          _id: valuation.product_id,
        }))}
        columns={getColumns()}
        loading={state.loading}
        sortConfig={{
          field: sortField,
          direction: sortOrder,
        }}
        onSort={handleSort}
        pagination={{
          total: state.total,
          page: state.page,
          pageSize: state.pageSize,
          onPageChange: handlePageChange,
          onPageSizeChange: handlePageSizeChange,
        }}
        toolbarActions={toolbarActions}
        styleOptions={{
          bordered: true,
          columnSeparators: false,
        }}
        emptyState={{
          icon: "inventory_2",
          title: "No Stock Valuation Data Found",
          description:
            "There are no inventory items matching your current filters. Try adjusting your search criteria.",
          actionLabel: "Clear Filters",
          onAction: clearFilters,
        }}
      />
    </Box>
  );
};

export default StockValuation;
